/**
 * Product module - Product fetching and card management
 */
import { fetchProduct } from '../services/api.js';
import { handleApiError } from '../services/errorHandler.js';
import { createProductCard, updatePricingDisplay, createElement } from '../utils/sanitize.js';
import { toastify } from '../helpers/toastify.js';
import { log } from '../utils/log.js';
import { getDossierId } from '../state.js';
import { ERROR_MESSAGES } from '../constants.js';
import { updateCardTotal, updateGlobalTotal } from './ui.js';
import { addProductToDossier, updateProductQuantity, removeProductFromDossier, handleDossierCreation } from './dossier.js';
import { showProductConfirmation } from './productConfirm.js';

/**
 * Fetches product data and shows confirmation modal
 * @param {string} scannedId - Scanned product/option ID
 */
export async function fetchProductData(scannedId) {
  log.info(`Fetching product data for scanned ID: ${scannedId}`);

  try {
    const product = await fetchProduct(scannedId);

    // Show confirmation modal instead of adding directly
    showProductConfirmation(
      product,
      // On confirm: add product with specified quantity
      async (confirmedProduct, quantity) => {
        await addProductToCard(confirmedProduct, quantity);
      },
      // On cancel: just log and continue
      () => {
        log.info('Product addition cancelled by user');
      }
    );
  } catch (error) {
    handleApiError(error, 'Fetch Product', {
      customMessage: ERROR_MESSAGES.PRODUCT_NOT_FOUND
    });
  }
}

/**
 * Adds a product as a card or updates quantity if exists
 * @param {object} product - Product data from API
 * @param {number} quantity - Quantity to add (default: 1)
 */
export async function addProductToCard(product, quantity = 1) {
  const container = document.getElementById('scanned-items-container');
  if (!container) return;

  const existingCard = container.querySelector(`[data-product-id="${product.option_id}"]`);
  const dossierId = getDossierId();

  if (existingCard) {
    // Update existing card quantity
    const quantityInput = existingCard.querySelector('.quantity-input');
    if (quantityInput) {
      quantityInput.value = parseInt(quantityInput.value, 10) + quantity;
      log.info(`Updated quantity for product ID: ${product.option_id} to ${quantityInput.value}`);
      updateCardTotal(existingCard);
      await updateProductQuantity(existingCard);
    }
  } else {
    // Create new card with specified quantity (prepend to show latest first)
    const card = createProductCard(product, { quantity });
    container.prepend(card);

    // Set up event listeners
    const quantityInput = card.querySelector('.quantity-input');
    const deleteButton = card.querySelector('.btn-close');

    const addQuantityListener = () => {
      quantityInput?.addEventListener('change', async () => {
        updateCardTotal(card);
        await updateProductQuantity(card);
      });
    };

    deleteButton?.addEventListener('click', () => removeProductFromDossier(card));

    if (dossierId) {
      await addProductToDossier(card);
      addQuantityListener();
    } else {
      addQuantityListener();
    }
  }

  updateGlobalTotal();

  if (!dossierId) {
    await handleDossierCreation();
  }
}

/**
 * Adds a dossier item to the card UI
 * @param {object} item - Dossier item data
 * @param {object} productDetails - Optional product details with option_reference
 */
export function addDossierItemToCard(item, productDetails = null) {
  const container = document.getElementById('scanned-items-container');
  if (!container) return;

  const quantity = parseInt(item.dossier_produit_qte, 10);
  const prixHt = parseFloat(item.dossier_produit_prix_ht);
  const remise = parseFloat(item.dossier_produit_remise);
  const prixNet = prixHt * (1 - remise / 100);
  const total = prixNet * quantity;

  // Use product details if available, fallback to item data
  const optionReference = productDetails?.option_reference || item.option_reference || '';
  const produitId = productDetails?.produit_id || item.produit_id || item.option_id;

  // Create card using safe DOM methods
  const card = createElement(
    'div',
    {
      className: 'col-12 col-md-6 col-lg-4 mb-3',
      dataset: {
        productId: item.option_id,
        dossierProduitId: item.dossier_produit_id
      }
    },
    [
      createElement('div', { className: 'card' }, [
        // Header
        createElement('div', { className: 'card-header d-flex justify-content-between align-items-center' }, [
          createElement('h5', { className: 'card-title mb-0' }, `${optionReference} [${produitId}]`),
          createElement('button', { type: 'button', className: 'btn-close', 'aria-label': 'Close' })
        ]),
        // Body
        createElement('div', { className: 'card-body' }, [
          createElement('p', { className: 'text-muted small' }, item.dossier_produit_nom || ''),
          createPricingParagraph(prixHt, remise, prixNet),
          createElement('div', { className: 'd-flex justify-content-between align-items-center' }, [
            createElement('div', { className: 'input-group w-50' }, [
              createElement('span', { className: 'input-group-text' }, 'Qté'),
              createElement('input', {
                type: 'number',
                className: 'form-control quantity-input',
                value: String(quantity),
                min: '1'
              })
            ]),
            createElement('p', { className: 'card-text mb-0' }, [
              createElement('strong', {}, 'Total: '),
              createElement('span', { className: 'total-card' }, total.toFixed(2))
            ])
          ])
        ])
      ])
    ]
  );

  container.prepend(card);
  log.info('Added product from dossier to card:', item);

  // Set up event listeners
  const quantityInput = card.querySelector('.quantity-input');
  quantityInput?.addEventListener('change', async () => {
    updateCardTotal(card);
    await updateProductQuantity(card);
  });

  const deleteButton = card.querySelector('.btn-close');
  deleteButton?.addEventListener('click', () => removeProductFromDossier(card));
}

/**
 * Creates a pricing paragraph element
 * @param {number} prixHt - Gross price
 * @param {number} remise - Discount percentage
 * @param {number} prixNet - Net price
 * @returns {HTMLElement} Pricing paragraph
 */
function createPricingParagraph(prixHt, remise, prixNet) {
  const p = createElement('p', { className: 'card-text' });

  p.appendChild(createElement('strong', {}, 'P. Brut:'));
  p.appendChild(document.createTextNode(` ${prixHt.toFixed(2)} `));
  p.appendChild(document.createElement('br'));

  p.appendChild(createElement('strong', {}, 'Remise:'));
  p.appendChild(document.createTextNode(` ${remise}% `));
  p.appendChild(document.createElement('br'));

  p.appendChild(createElement('strong', { className: 'prix-net-label' }, 'P. Net:'));
  p.appendChild(document.createTextNode(' '));
  p.appendChild(createElement('span', { className: 'prix-net' }, prixNet.toFixed(2)));

  return p;
}

/**
 * Updates card with detailed product information from API response
 * @param {HTMLElement} card - Card element
 * @param {object} productDetails - Product details from API
 */
export function updateCardWithProductDetails(card, productDetails) {
  // Set the dossier product ID
  card.setAttribute('data-dossier-produit-id', productDetails.dossier_produit_id);

  // Card title is already set correctly by createProductCard, no need to update

  // Update pricing information
  const cardBody = card.querySelector('.card-body');
  if (cardBody) {
    const prixNet = parseFloat(productDetails.final_price) / parseInt(productDetails.quantity, 10);
    const total = parseFloat(productDetails.final_price);
    const prixHt = parseFloat(productDetails.original_price);
    const remise = parseFloat(productDetails.applied_discount);

    // Update pricing display
    updatePricingDisplay(cardBody, prixHt, remise, prixNet);

    // Update total
    const totalElement = cardBody.querySelector('.total-card');
    if (totalElement) {
      totalElement.textContent = total.toFixed(2);
    }

    // Update quantity if changed
    const quantityInput = cardBody.querySelector('.quantity-input');
    if (quantityInput && parseInt(quantityInput.value, 10) !== productDetails.quantity) {
      quantityInput.value = productDetails.quantity;
    }
  }

  updateGlobalTotal();
}
